import type { RoutingStatusResponse } from '@lousada/shared';
import jsdLogo from '../assets/jsd-lousada.png';
import { BrandMark } from '../ui/BrandMark.js';
import { freshnessLine, validityLine } from './freshness.js';

export interface PlannerFooterProps {
  status: RoutingStatusResponse | null;
  onNavigate?: ((path: string) => void) | undefined;
  /** The top bar carries the link on wide screens; the sheet has no top bar links. */
  showStatusLink: boolean;
}

export function StatusLink({
  onNavigate,
  className,
}: {
  onNavigate?: ((path: string) => void) | undefined;
  className: string;
}): React.JSX.Element {
  return (
    <a
      className={className}
      href="/status"
      onClick={(event) => {
        if (onNavigate === undefined) return;
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
        event.preventDefault();
        onNavigate('/status');
      }}
    >
      Estado do serviço
    </a>
  );
}

export default function PlannerFooter({
  status,
  onNavigate,
  showStatusLink,
}: PlannerFooterProps): React.JSX.Element {
  const freshness = freshnessLine(status);
  const validity = validityLine(status);
  return (
    <footer className="colophon">
      {freshness !== null && (
        <p className="colophon__data">
          {freshness}
          {validity === null ? '' : `, ${validity}`}.
        </p>
      )}
      <p>
        Projeto da JSD Lousada, sem fins lucrativos. Não é um serviço da Câmara Municipal de
        Lousada.
        {showStatusLink && (
          <>
            {' '}
            <StatusLink className="link" onNavigate={onNavigate} />
          </>
        )}
      </p>
    </footer>
  );
}

const SOCIALS = [
  {
    name: 'Instagram',
    href: 'https://www.instagram.com/jsdlousada/',
    path: 'M12 2.2c3.2 0 3.6 0 4.8.1 3.3.1 4.8 1.7 4.9 4.9.1 1.3.1 1.6.1 4.8s0 3.6-.1 4.8c-.1 3.2-1.7 4.8-4.9 4.9-1.3.1-1.6.1-4.8.1s-3.6 0-4.8-.1c-3.3-.1-4.8-1.7-4.9-4.9C2.2 15.6 2.2 15.2 2.2 12s0-3.6.1-4.8C2.4 3.9 3.9 2.4 7.2 2.3c1.2-.1 1.6-.1 4.8-.1zM12 0C8.7 0 8.3 0 7.1.1 2.7.3.3 2.7.1 7.1 0 8.3 0 8.7 0 12s0 3.7.1 4.9c.2 4.4 2.6 6.8 7 7 1.2.1 1.6.1 4.9.1s3.7 0 4.9-.1c4.4-.2 6.8-2.6 7-7 .1-1.2.1-1.6.1-4.9s0-3.7-.1-4.9c-.2-4.4-2.6-6.8-7-7C15.7 0 15.3 0 12 0zm0 5.8a6.2 6.2 0 1 0 0 12.4 6.2 6.2 0 0 0 0-12.4zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.4-11.8a1.4 1.4 0 1 0 0 2.9 1.4 1.4 0 0 0 0-2.9z',
  },
  {
    name: 'Facebook',
    href: 'https://www.facebook.com/jsdlousada',
    path: 'M24 12.07C24 5.4 18.63 0 12 0S0 5.4 0 12.07C0 18.1 4.39 23.1 10.13 24v-8.44H7.08v-3.49h3.05V9.41c0-3.02 1.79-4.7 4.53-4.7 1.31 0 2.68.24 2.68.24v2.97h-1.51c-1.49 0-1.95.93-1.95 1.88v2.27h3.33l-.53 3.49h-2.8V24C19.61 23.1 24 18.1 24 12.07z',
  },
  {
    name: 'TikTok',
    href: 'https://www.tiktok.com/@jsdlousada',
    path: 'M12.53.02C13.84 0 15.14.01 16.44 0c.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z',
  },
];

/** Product first, authorship second: the wordmark, then who built it, then the data credits. */
export function SiteFooter(): React.JSX.Element {
  return (
    <div className="site-footer">
      <div className="site-footer__authors">
        <span className="wordmark wordmark--small">
          <BrandMark size={30} />
          Mobilidade Lousada
        </span>
        <span className="site-footer__divider" aria-hidden="true" />
        <span className="initiative">
          Uma iniciativa da
          <img className="initiative__logo" src={jsdLogo} alt="JSD Lousada" />
        </span>
      </div>
      <div className="site-footer__credits">
        <p>Horários: Município de Lousada / CIM do Tâmega e Sousa (LINHAS), CC BY 4.0.</p>
        <p>Mapa e locais: © OpenStreetMap, ODbL.</p>
      </div>
      <ul className="socials" aria-label="JSD Lousada nas redes sociais">
        {SOCIALS.map((social) => (
          <li key={social.name}>
            <a
              className="socials__link"
              href={social.href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={social.name}
              title={social.name}
            >
              <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                <path fill="currentColor" d={social.path} />
              </svg>
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}
